import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Search,
  TableProperties,
  ChevronLeft,
  ChevronRight,
  Upload,
  Plus,
  Edit,
  Eye,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddOneButton from "../../common/AddOneButton";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import ProductListSkeleton from "../DashBoardProduct/ProductListSkeleton";
import StockChangeModal from "../../AdminSideComponents/DashBoardProduct/StockChangeModal";
import BulkUploadModal from "../../AdminSideComponents/DashBoardProduct/BulkUploadModal";

const ProductList = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10); // Amazon style lists show more entries
  const [error, setError] = useState(null);

  // Modal states
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [cHngModalData, setCHngModalData] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  // Delete Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const navigate = useNavigate();
  const getLoginData = JSON.parse(localStorage.getItem("login-data"));
  const isAdmin = getLoginData?.userType !== "supplier";

  // Helper function to extract English text from multilingual object
  const getEnglishText = (field) => {
    if (!field) return "";
    if (typeof field === "object" && field !== null && field.en !== undefined) {
      return field.en;
    }
    if (typeof field === "string") {
      return field;
    }
    return "";
  };

  const getUploadedByLabel = (product) => {
    if (product?.uploadedBy === "supplier") {
      return (
        product?.supplier?.businessName ||
        product?.supplier?.fullName ||
        "Supplier"
      );
    }
    return "Admin";
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-all-products`,
      );
      setProducts(response.data?.products || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-all-categories`,
      );
      setCategories(response.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  }, []);

  const fetchSubCategories = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`,
      );
      setSubCategories(response.data || []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSubCategories();
  }, [fetchProducts, fetchCategories, fetchSubCategories]);

  const suppliers = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (p.uploadedBy === "supplier" && p.supplier?._id) {
        if (!map.has(p.supplier._id)) {
          map.set(p.supplier._id, {
            _id: p.supplier._id,
            name: p.supplier.businessName || p.supplier.fullName || "Supplier",
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [products]);

  useEffect(() => {
    if (selectedCategory) {
      const filtered = subCategories.filter(
        (sub) => sub.category?._id === selectedCategory,
      );
      setFilteredSubCategories(filtered);
    } else {
      setFilteredSubCategories([]);
    }
    setSelectedSubCategory("");
  }, [selectedCategory, subCategories]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const productName = getEnglishText(p.name);
      const categoryName = getEnglishText(p.category?.name);
      const subCategoryName = getEnglishText(p.subCategory?.name);

      const searchMatch =
        productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.bhkg_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subCategoryName?.toLowerCase().includes(searchTerm.toLowerCase());

      const stockMatch =
        !stockStatus ||
        (stockStatus === "active" && p.stock_status === "active") ||
        (stockStatus === "inactive" && p.stock_status === "inactive") ||
        (stockStatus === "out of stock" && p.stock_status === "out of stock");

      const categoryMatch =
        !selectedCategory || p.category?._id === selectedCategory;

      const subCategoryMatch =
        !selectedSubCategory || p.subCategory?._id === selectedSubCategory;

      const supplierMatch =
        !selectedSupplier || p.supplier?._id === selectedSupplier;

      return (
        searchMatch &&
        stockMatch &&
        categoryMatch &&
        subCategoryMatch &&
        supplierMatch
      );
    });
  }, [
    products,
    searchTerm,
    stockStatus,
    selectedCategory,
    selectedSubCategory,
    selectedSupplier,
  ]);

  const totalPages = Math.ceil(filteredProducts.length / entriesPerPage);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    return filteredProducts.slice(startIndex, startIndex + entriesPerPage);
  }, [filteredProducts, currentPage, entriesPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStockStatus("");
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedSupplier("");
    setCurrentPage(1);
  };

  const handleOpenStockModal = (product) => {
    setCHngModalData({
      prod_id: product._id,
      prod_status: product.stock_status,
      prod_name: getEnglishText(product?.name),
    });
    setIsStockModalOpen(true);
  };

  const handleDeleteProduct = (id) => {
    setProductToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/delete-product/${productToDelete}`
      );
      setProducts(products.filter((p) => p._id !== productToDelete));
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("Failed to delete product.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-[#f3f3f3] font-sans">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <TableProperties className="text-primaryColor w-7 h-7" />
          All Products
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-auto sm:ml-auto">
            <AddOneButton
              onClick={() => navigate("/add-products")}
              label="Add Product"
            />
          </div>
          <button
            onClick={() => setIsBulkUploadOpen(true)}
            className="bg-[#b07d17] hover:bg-[#8c6312] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-sm"
          >
            <Upload className="w-5 h-5" />
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-300 mb-4 bg-transparent overflow-x-auto">
        <button
          onClick={() => { setStockStatus(""); setCurrentPage(1); }}
          className={`whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors border-b-2 ${stockStatus === ""
              ? "text-[#007185] border-[#007185]"
              : "text-gray-600 border-transparent hover:text-[#007185] hover:border-gray-300"
            }`}
        >
          All Inventory
        </button>
        <button
          onClick={() => { setStockStatus("active"); setCurrentPage(1); }}
          className={`whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors border-b-2 ${stockStatus === "active"
              ? "text-[#007185] border-[#007185]"
              : "text-gray-600 border-transparent hover:text-[#007185] hover:border-gray-300"
            }`}
        >
          Active
        </button>
        <button
          onClick={() => { setStockStatus("inactive"); setCurrentPage(1); }}
          className={`whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors border-b-2 ${stockStatus === "inactive"
              ? "text-[#007185] border-[#007185]"
              : "text-gray-600 border-transparent hover:text-[#007185] hover:border-gray-300"
            }`}
        >
          Inactive
        </button>
        <button
          onClick={() => { setStockStatus("out of stock"); setCurrentPage(1); }}
          className={`whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors border-b-2 ${stockStatus === "out of stock"
              ? "text-[#007185] border-[#007185]"
              : "text-gray-600 border-transparent hover:text-[#007185] hover:border-gray-300"
            }`}
        >
          Out of Stock
        </button>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border border-[#D5D9D9] p-4 rounded-md mb-4 shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search Bar - Amazon style */}
          <div className="flex flex-1 min-w-[280px]">
            <input
              type="text"
              placeholder="Search SKU, Title, ASIN..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="flex-1 border border-[#D5D9D9] border-r-0 rounded-l-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#007185] focus:border-[#007185]"
            />
            <button className="bg-gray-100 border border-[#D5D9D9] px-3 py-1.5 rounded-r-md hover:bg-gray-200 transition text-gray-700">
              <Search className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-wrap gap-3 items-center ml-auto">
            <span className="text-sm font-medium text-gray-700">Filter by:</span>

            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-[#D5D9D9] bg-gray-50 rounded-md px-2 py-1.5 text-sm w-36 focus:outline-none hover:bg-gray-100 transition shadow-sm cursor-pointer"
            >
              <option value="">Category (All)</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {getEnglishText(cat.name)}
                </option>
              ))}
            </select>

            <select
              value={selectedSubCategory}
              onChange={(e) => {
                setSelectedSubCategory(e.target.value);
                setCurrentPage(1);
              }}
              disabled={!selectedCategory || filteredSubCategories.length === 0}
              className="border border-[#D5D9D9] bg-gray-50 rounded-md px-2 py-1.5 text-sm w-36 focus:outline-none hover:bg-gray-100 transition shadow-sm cursor-pointer disabled:opacity-50"
            >
              <option value="">
                {!selectedCategory ? "SubCategory" : filteredSubCategories.length === 0 ? "No SubCategories" : "SubCategory (All)"}
              </option>
              {filteredSubCategories.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {getEnglishText(sub.name)}
                </option>
              ))}
            </select>

            {isAdmin && (
              <select
                value={selectedSupplier}
                onChange={(e) => {
                  setSelectedSupplier(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-[#D5D9D9] bg-gray-50 rounded-md px-2 py-1.5 text-sm w-36 focus:outline-none hover:bg-gray-100 transition shadow-sm cursor-pointer"
              >
                <option value="">Supplier (All)</option>
                {suppliers.map((sup) => (
                  <option key={sup._id} value={sup._id}>
                    {sup.name}
                  </option>
                ))}
              </select>
            )}

            {(searchTerm || selectedCategory || selectedSubCategory || selectedSupplier) && (
              <button
                onClick={handleResetFilters}
                className="text-sm text-[#007185] hover:text-[#c45500] hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Table Area */}
      <div className="bg-white border border-[#D5D9D9] rounded-md shadow-sm overflow-hidden">
        {/* Table Toolbar */}
        <div className="bg-gray-50 border-b border-[#D5D9D9] px-4 py-2 flex justify-between items-center text-sm">
          <div className="text-gray-700">
            Showing <span className="font-bold">{paginatedProducts.length}</span> of <span className="font-bold">{filteredProducts.length}</span> results
          </div>
          {/* Top Pagination */}
          {!loading && filteredProducts.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-[#D5D9D9] rounded bg-white hover:bg-gray-50 disabled:opacity-50 shadow-sm"
              >
                &lt;
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-[#D5D9D9] rounded bg-white hover:bg-gray-50 disabled:opacity-50 shadow-sm"
              >
                &gt;
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <ProductListSkeleton />
          ) : error ? (
            <div className="p-10 text-center text-red-500">{error}</div>
          ) : filteredProducts.length > 0 ? (
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-[#f3f3f3] text-gray-800 border-b border-[#D5D9D9]">
                <tr>
                  <th className="px-3 py-2 font-bold w-20 text-center">Status</th>
                  <th className="px-3 py-2 font-bold w-16">Image</th>
                  <th className="px-3 py-2 font-bold">Product Name</th>
                  <th className="px-3 py-2 font-bold">Category</th>
                  <th className="px-3 py-2 font-bold">Price</th>
                  <th className="px-3 py-2 font-bold">Stock</th>
                  <th className="px-3 py-2 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product) => {
                  const productName = getEnglishText(product?.name);
                  const categoryName = getEnglishText(product.category?.name);
                  const subCategoryName = getEnglishText(product.subCategory?.name);
                  const uploadedByLabel = getUploadedByLabel(product);

                  // Status style
                  let statusColor = "text-gray-500";
                  let statusText = "Inactive";
                  if (product.stock_status === "active") {
                    statusColor = "text-green-700";
                    statusText = "Active";
                  } else if (product.stock_status === "out of stock") {
                    statusColor = "text-red-700";
                    statusText = "Out of Stock";
                  }

                  return (
                    <tr
                      key={product._id}
                      className="border-b border-[#e7e7e7] hover:bg-gray-50 transition"
                    >
                      {/* Status Column */}
                      <td className="px-3 py-3 text-center align-top">
                        <div className={`font-medium ${statusColor} text-xs uppercase tracking-wider`}>
                          {statusText}
                        </div>
                      </td>

                      {/* Image Column */}
                      <td className="px-3 py-3 align-top">
                        <div className="w-14 h-14 border border-gray-200 rounded p-1 bg-white flex items-center justify-center">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt="product"
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <span className="text-xs text-gray-400">No Img</span>
                          )}
                        </div>
                      </td>

                      {/* Product Name & SKU */}
                      <td className="px-3 py-3 align-top">
                        <a
                          onClick={() =>
                            getLoginData?.userType === "supplier"
                              ? navigate(`/seller/product-details/${product._id}`)
                              : navigate(`/admin/product-details/${product._id}`)
                          }
                          className="text-[#007185] hover:text-[#c45500] hover:underline cursor-pointer font-medium text-base leading-tight block mb-1"
                        >
                          {productName.length > 80 ? productName.slice(0, 80) + "..." : productName}
                        </a>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                          <div><span className="text-gray-400">SKU:</span> {product.sku || "--"}</div>
                          <div><span className="text-gray-400">ID:</span> {product.bhkg_id || "--"}</div>
                          <div><span className="text-gray-400">By:</span> {uploadedByLabel}</div>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-3 py-3 align-top text-gray-700">
                        <div className="font-medium">{categoryName || "--"}</div>
                        <div className="text-xs text-gray-500 mt-1">{subCategoryName || "--"}</div>
                      </td>

                      {/* Price */}
                      <td className="px-3 py-3 align-top font-bold text-gray-900">
                        ₹{product.price}
                      </td>

                      {/* Stock / Available */}
                      <td className="px-3 py-3 align-top">
                        <div className="font-medium text-gray-800">{product.stock || 0}</div>
                      </td>

                      {/* Action */}
                      <td className="px-3 py-3 align-top">
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            onClick={() =>
                              getLoginData?.userType === "supplier"
                                ? navigate(`/seller/product-details/${product._id}`)
                                : navigate(`/admin/product-details/${product._id}`)
                            }
                            title="View Product"
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              getLoginData?.userType === "supplier"
                                ? navigate(`/seller/edit-product/${product._id}`)
                                : navigate(`/edit-product/${product._id}`)
                            }
                            title="Edit Product"
                            className="text-green-600 hover:text-green-800 p-1"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product._id)}
                            title="Delete Product"
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleOpenStockModal(product)}
                          className="bg-white border border-[#D5D9D9] hover:bg-gray-50 text-gray-800 rounded px-3 py-1 shadow-sm transition text-xs font-medium w-full text-center"
                        >
                          Edit Status
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center">
              <p className="text-gray-500 mb-4">No products found matching your criteria.</p>
              <button
                onClick={handleResetFilters}
                className="bg-white border border-[#D5D9D9] px-4 py-1.5 rounded-full text-sm hover:bg-gray-50 transition"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        {!loading && filteredProducts.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-white">
            <h6 className="text-sm text-gray-500">
              Showing {paginatedProducts.length} of {filteredProducts.length}{" "}
              entries
            </h6>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === i + 1
                    ? "bg-blue-500 text-white"
                    : "border border-gray-300 hover:bg-gray-100"
                    }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <StockChangeModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        modalData={cHngModalData}
        setProducts={setProducts}
      />
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        setProducts={setProducts}
      />
      <DeleteAlertModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        title="Delete Product"
        text="Are you sure you want to delete this product? This action cannot be undone."
        onConfirm={confirmDelete}
        dltLoading={deleteLoading}
      />
    </section>
  );
};

export default ProductList;
